import { z } from 'zod';

/**
 * The standard list-API contract — see docs/adr/0001-standard-list-api-contract.md.
 *
 * These factories are the single statement of the envelope: the backend builds
 * procedure inputs/outputs from them, and the frontend derives its filter types,
 * sort enums, and URL codecs from the same schemas rather than restating them.
 * Keep this file pure Zod with no other imports — it ships in browser bundles.
 */

export const SortDirSchema = z.enum(['asc', 'desc']);
export type SortDir = z.infer<typeof SortDirSchema>;

/** Default page size for collection lists; also the schema default for `limit`. */
export const DEFAULT_LIST_LIMIT = 25;

/**
 * A `{ by, dir }` sort schema over a domain's sortable fields. The runtime field
 * list is recoverable from the built schema (`schema.def.innerType.shape.by.options`),
 * so UI never hand-copies the enum.
 */
export function sortSchema<const T extends readonly [string, ...string[]]>(
  fields: T,
  defaultBy: T[number],
  defaultDir: SortDir = 'desc'
) {
  return z
    .object({
      by: z.enum(fields).default(defaultBy),
      dir: SortDirSchema.default(defaultDir),
    })
    .default({ by: defaultBy, dir: defaultDir });
}

/**
 * The standard input envelope: domain filters nested under `filter`, mechanics
 * (`sort`/`cursor`/`limit`) alongside them so the two can never collide.
 *
 * `filter` is defaulted rather than optional so react-query keys stay stable
 * (`{ filter: undefined }` and `{}` would otherwise be different keys). Every
 * filter field is optional by contract — absent means "no constraint" — which is
 * what makes `{}` a valid default for any `F`.
 */
export function listInputSchema<F extends z.ZodObject, S extends z.ZodType>(filter: F, sort: S) {
  return z.object({
    // Every filter field is optional by contract, so `{}` always parses — but
    // TypeScript can't prove that through an unresolved `F`, hence the cast.
    filter: filter.default({} as never),
    sort,
    cursor: z.number().int().min(0).default(0),
    limit: z.number().int().min(1).max(100).default(DEFAULT_LIST_LIMIT),
  });
}

export function listOutputSchema<T extends z.ZodType>(item: T) {
  return z.object({
    items: z.array(item),
    nextCursor: z.number().nullable(),
    totalCount: z.number(),
  });
}

/**
 * Position/paging facts a list UI needs, derived from what the server returned.
 * Offset cursors are what make prev-page and `pageStart`/`pageEnd` computable
 * client-side without the server echoing the request back (ADR-0001 §4).
 */
export function deriveListPage(
  cursor: number,
  items: unknown[],
  totalCount: number,
  nextCursor: number | null | undefined
) {
  return {
    pageStart: totalCount === 0 ? 0 : cursor + 1,
    pageEnd: cursor + items.length,
    hasNextPage: nextCursor != null,
    hasPrevPage: cursor > 0,
  };
}
